import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * SD Image Accordion - An accordion component where each row has its own image or video.
 *
 * On desktop: Media displays in a shared container that switches based on the active accordion.
 * On mobile: Media renders inside each accordion's content area.
 * Only one accordion can be open at a time (mutual exclusivity).
 *
 * Each row video renders once, unloaded, and is cloned into the desktop panel like an image.
 * An unloaded `deferred-media` clone is an independent, correctly wired element; moving one
 * would abort its listeners for good. The accordion owns playback: only the visible copy of
 * the open row loads or plays, and every other copy is paused.
 *
 * @class SdImageAccordion
 * @extends Component
 */
class SdImageAccordion extends Component {
  /** @type {number} Currently active row index (-1 means none open) */
  #activeIndex = 0;

  /** @type {HTMLElement[]} All accordion row elements */
  #rows = [];

  /** @type {HTMLElement[]} All image wrapper elements (desktop) */
  #imageWrappers = [];

  /** @type {HTMLElement} Desktop image container */
  #imageContainer = null;

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {Set<number>} Rows whose video the visitor paused, so the accordion won't resume them */
  #userPaused = new Set();

  /** @type {boolean} Whether `deferred-media` is defined, so its methods exist on the clones */
  #mediaReady = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Cache row elements and image container
    this.#rows = Array.from(this.querySelectorAll('.sd-image-accordion-row'));
    this.#imageContainer = this.querySelector('.sd-image-accordion__image-container');

    // Prepare YouTube/Vimeo templates before cloning so both copies share the result
    this.#prepareExternalVideos();

    // Build desktop image wrappers from row images
    this.#buildDesktopImages();

    // Track visitor pauses on uploaded videos (`pause` doesn't bubble, so capture)
    this.addEventListener('pause', this.#handlePause, { capture: true, signal });
    this.addEventListener('play', this.#handlePlay, { capture: true, signal });

    // Keep autoplay rows from pausing the page's other videos
    this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStarted, { signal });

    // Find the initially open row (from Liquid's open_by_default setting)
    this.#activeIndex = this.#rows.findIndex((row) => row.classList.contains('sd-image-accordion-row--open'));

    // If no row is open by default, open the first one
    if (this.#activeIndex === -1 && this.#rows.length > 0) {
      this.#activeIndex = 0;
    }

    // Set initial state (this ensures everything is in sync)
    this.#syncState();

    // Add click listeners to all accordion headers
    for (const row of this.#rows) {
      const header = row.querySelector('.sd-image-accordion-row__header');
      if (header) {
        header.addEventListener('click', this.#handleToggle, { signal });
      }
    }

    // Listen for media query changes to sync state
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Clones may upgrade after this runs, so wait before calling deferred-media methods
    customElements.whenDefined('deferred-media').then(() => {
      if (signal.aborted) return;
      this.#mediaReady = true;
      this.#syncMedia();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Builds the desktop image wrappers by cloning images from row mobile containers.
   */
  #buildDesktopImages() {
    if (!this.#imageContainer) return;

    // Get the placeholder from the container (we'll hide it if we have images)
    const placeholder = this.#imageContainer.querySelector('.sd-image-accordion__placeholder');

    for (const [index, row] of this.#rows.entries()) {
      const mobileVideo = row.querySelector('.sd-image-accordion-row__mobile-image .sd-image-accordion-row__video');
      const mobileImage = row.querySelector('.sd-image-accordion-row__mobile-image img');
      const mobilePlaceholder = row.querySelector('.sd-image-accordion-row__placeholder');

      // Create wrapper for this row's image
      const wrapper = document.createElement('div');
      wrapper.className = 'sd-image-accordion__image-wrapper';
      wrapper.dataset.rowIndex = index;

      if (mobileVideo) {
        // Clone the unloaded video for desktop use
        const desktopVideo = /** @type {HTMLElement} */ (mobileVideo.cloneNode(true));
        desktopVideo.className = 'sd-image-accordion__video';
        wrapper.appendChild(desktopVideo);
      } else if (mobileImage) {
        // Clone the image for desktop use
        const desktopImage = mobileImage.cloneNode(true);
        desktopImage.className = 'sd-image-accordion__image';
        // Update sizes for desktop context
        desktopImage.sizes = '(min-width: 990px) 50vw, 100vw';
        // Desktop images should be eager for active, lazy for others
        desktopImage.loading = index === 0 ? 'eager' : 'lazy';
        wrapper.appendChild(desktopImage);
      } else if (mobilePlaceholder) {
        // Clone the placeholder for desktop use
        const desktopPlaceholder = mobilePlaceholder.cloneNode(true);
        desktopPlaceholder.className = 'sd-image-accordion__row-placeholder';
        wrapper.appendChild(desktopPlaceholder);
      }

      this.#imageContainer.appendChild(wrapper);
    }

    // Hide the default placeholder if we have any image wrappers
    if (placeholder && this.#rows.length > 0) {
      placeholder.style.display = 'none';
    }

    // Cache the image wrappers
    this.#imageWrappers = Array.from(this.#imageContainer.querySelectorAll('.sd-image-accordion__image-wrapper'));
  }

  /**
   * Handles media query changes to ensure proper state sync.
   */
  #handleMediaQueryChange = () => {
    this.#syncState();
  };

  /**
   * Synchronizes the visual state based on the active index.
   */
  #syncState() {
    // Update rows
    for (const [index, row] of this.#rows.entries()) {
      const isActive = index === this.#activeIndex;
      const trigger = row.querySelector('.sd-image-accordion-row__header');
      const content = row.querySelector('.sd-image-accordion-row__content');

      row.classList.toggle('sd-image-accordion-row--open', isActive);

      if (trigger) {
        trigger.setAttribute('aria-expanded', String(isActive));
      }

      if (content) {
        content.inert = !isActive;
      }
    }

    // Update desktop images
    for (const [index, wrapper] of this.#imageWrappers.entries()) {
      const isActive = index === this.#activeIndex;
      wrapper.classList.toggle('sd-image-accordion__image-wrapper--active', isActive);
    }

    this.#syncMedia();
  }

  /**
   * Readies each YouTube/Vimeo row's template before it is cloned.
   *
   * The `video` snippet's URL iframe has no `data-video-type`, which `deferred-media` needs to
   * pause or resume it over postMessage, so it is stamped here from the snippet's
   * `js-youtube` / `js-vimeo` class. The row always renders with the snippet's autoplay off
   * (to stay deferred), so the URL carries neither autoplay nor mute. Every row gets autoplay,
   * since the iframe only loads on a click or when an autoplay row opens; autoplay rows also
   * get mute, since browsers block unmuted playback that no click started.
   */
  #prepareExternalVideos() {
    for (const row of this.#rows) {
      const container = row.querySelector('.sd-image-accordion-row__video');
      const template = container?.querySelector('deferred-media template');
      if (!(container instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) continue;

      const iframe = template.content.querySelector('iframe');
      // Only the snippet's URL iframe needs this, and only once
      if (!iframe || iframe.dataset.videoType) continue;
      if (!iframe.matches('.js-youtube, .js-vimeo')) continue;

      const isYouTube = iframe.classList.contains('js-youtube');
      iframe.dataset.videoType = isYouTube ? 'youtube' : 'vimeo';
      iframe.src += '&autoplay=1';

      if (container.dataset.videoAutoplay === 'true') {
        iframe.src += isYouTube ? '&mute=1' : '&muted=1';
      }
    }
  }

  /**
   * Returns the row's `deferred-media` copy that the current breakpoint shows.
   *
   * @param {number} index - Row index
   * @returns {HTMLElement | null}
   */
  #getVisibleMedia(index) {
    const source = mediaQueryLarge.matches ? this.#imageWrappers[index] : this.#rows[index];
    return source?.querySelector('deferred-media') ?? null;
  }

  /**
   * Pauses every video copy except the visible copy of the open row, then starts that copy
   * when the editor turned autoplay on and the visitor hasn't paused it.
   */
  #syncMedia() {
    if (!this.#mediaReady) return;

    const activeMedia = this.#getVisibleMedia(this.#activeIndex);

    for (const media of this.querySelectorAll('deferred-media')) {
      if (media === activeMedia || !media.hasAttribute('data-media-loaded')) continue;
      /** @type {any} */ (media).pauseMedia();
    }

    if (!activeMedia || this.#userPaused.has(this.#activeIndex)) return;

    const container = activeMedia.closest('.sd-image-accordion-row__video, .sd-image-accordion__video');
    if (!(container instanceof HTMLElement) || container.dataset.videoAutoplay !== 'true') return;

    const media = /** @type {any} */ (activeMedia);
    if (!media.hasAttribute('data-media-loaded')) media.loadContent(false);
    // Also sets the theme's play/pause hint, which loadContent alone leaves hidden
    media.playMedia();
  }

  /**
   * Returns the row index a media event came from, or -1.
   *
   * @param {Event} event
   * @returns {number}
   */
  #getEventRowIndex(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const wrapper = target.closest('.sd-image-accordion__image-wrapper');
    if (wrapper instanceof HTMLElement) return Number(wrapper.dataset.rowIndex);

    const row = target.closest('.sd-image-accordion-row');
    return row instanceof HTMLElement ? this.#rows.indexOf(row) : -1;
  }

  /**
   * Records a visitor pause on an uploaded video.
   *
   * `pause` fires asynchronously, so a flag set around the accordion's own `pauseMedia()`
   * calls would already be cleared. Instead, only a pause of the copy that is still the
   * visible copy of the open row counts: the accordion never pauses that one. A hidden tab
   * or a finished video is not a visitor choice. YouTube/Vimeo pauses aren't observable.
   *
   * @param {Event} event
   */
  #handlePause = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    const index = this.#getEventRowIndex(event);
    if (index !== this.#activeIndex) return;
    if (video.closest('deferred-media') !== this.#getVisibleMedia(index)) return;
    if (document.visibilityState !== 'visible' || video.ended) return;

    this.#userPaused.add(index);
  };

  /**
   * Stops an autoplay row's "media started playing" event at the accordion.
   *
   * `deferred-media` announces on `document` whenever it loads, and every other copy on the
   * page pauses in response. Horizon only announces on a visitor's click, since it renders
   * autoplay videos eagerly; the accordion loads autoplay rows itself, so without this each
   * page load and row open would pause autoplay videos elsewhere on the page. Click-to-play
   * rows still announce. The accordion pauses its own other copies in `#syncMedia()`.
   *
   * @param {Event} event
   */
  #handleMediaStarted = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const isAutoplay = target.closest(
      '.sd-image-accordion-row__video[data-video-autoplay="true"], .sd-image-accordion__video[data-video-autoplay="true"]'
    );
    if (isAutoplay) event.stopPropagation();
  };

  /**
   * Clears a visitor pause once that row's video plays again.
   *
   * @param {Event} event
   */
  #handlePlay = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    this.#userPaused.delete(this.#getEventRowIndex(event));
  };

  /**
   * Handles accordion row toggle click.
   *
   * @param {Event} event - The click event
   */
  #handleToggle = (event) => {
    const trigger = event.currentTarget;
    const row = trigger.closest('.sd-image-accordion-row');

    if (!row) return;

    // Find the index of this row in our cached array
    const rowIndex = this.#rows.indexOf(row);

    if (rowIndex === -1) return;

    // If clicking the already active row, do nothing (always keep one open)
    if (rowIndex === this.#activeIndex) {
      return;
    }

    // Open this row, close others
    this.#activeIndex = rowIndex;
    this.#syncState();
  };
}

if (!customElements.get('sd-image-accordion')) {
  customElements.define('sd-image-accordion', SdImageAccordion);
}
